import { useEffect, useRef, useState } from "react";
import {
  FiSearch,
  FiTrash,
  FiFilter,
  FiGrid,
  FiList,
  FiX,
  FiEdit2,
  FiPlus,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEye,
  FiUploadCloud,
  FiUserCheck,
} from "react-icons/fi";
import {
  getContainers,
  createContainer,
  updateContainer,
  deleteContainer,
  getAssignableCustomers,
  bulkAssignContainersToCustomer,
} from "../services/containerApi";
import api from "../services/api";
import AddContainerModal from "../components/AddContainerModal";
import ContainerCard from "../components/ContainerCard";
import ContainerDocumentsPanel from "../components/ContainerDocumentsPanel";
import ContainerCollaboratorsPanel from "../components/ContainerCollaboratorsPanel";
import ContainerStatusUpdatePanel from "../components/ContainerStatusUpdatePanel";

const PAGE_LIMIT = 10;

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getRoleFromToken = () => {
  try {
    const token = localStorage.getItem("token");

    if (!token || !token.includes(".")) return "";

    const payload = JSON.parse(atob(token.split(".")[1]));

    return payload?.roleName || payload?.role || payload?.userRole || "";
  } catch {
    return "";
  }
};

const getCurrentRole = () => {
  const possibleUserKeys = [
    "user",
    "authUser",
    "currentUser",
    "userData",
    "admin",
  ];

  for (const key of possibleUserKeys) {
    const user = safeJsonParse(localStorage.getItem(key));

    if (user?.roleName) return user.roleName;
    if (user?.role) return user.role;
    if (user?.userRole) return user.userRole;
    if (user?.type) return user.type;
  }

  return localStorage.getItem("role") || getRoleFromToken() || "";
};

const getCurrentUser = () => {
  const possibleUserKeys = ["user", "authUser", "currentUser", "userData", "admin"];
  for (const key of possibleUserKeys) {
    const user = safeJsonParse(localStorage.getItem(key));
    if (user) return user;
  }
  return {};
};

const checkIsSuperAdmin = () =>
  String(getCurrentUser()?.role || getCurrentRole()).toLowerCase() === "super_admin";

const checkIsCustomer = () => {
  const user = getCurrentUser();
  return String(user?.roleName || user?.role || getCurrentRole()).toLowerCase() === "customer";
};

const checkIsAssignedAccess = () =>
  String(getCurrentUser()?.accessScope || "all").toLowerCase() === "assigned";

const hasPermission = (moduleKey, action) => {
  const user = getCurrentUser();
  if (user?.permissions === "ALL" || checkIsSuperAdmin()) return true;
  const actionKey = {
    read: "canRead",
    create: "canCreate",
    update: "canUpdate",
    delete: "canDelete",
  }[action];
  return Boolean(actionKey && user?.permissions?.[moduleKey]?.[actionKey]);
};

const formatContainerType = (type) => {
  if (type === "40ft High Cube") return "40ft HC";
  return type || "N/A";
};

const getEtaState = (container) => {
  if (container.status === "Delivered") return "delivered";
  if (!container.expectedArrival) return "normal";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eta = new Date(container.expectedArrival);
  eta.setHours(0, 0, 0, 0);

  if (Number.isNaN(eta.getTime())) return "normal";

  const diffDays = Math.ceil(
    (eta.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "dueSoon";

  return "normal";
};

const getRowColorClass = (container) => {
  const state = getEtaState(container);

  if (state === "delivered") {
    return "border-l-4 border-green-400 bg-green-50/30 hover:bg-green-50 dark:bg-green-950/10 dark:hover:bg-green-950/20";
  }

  if (state === "overdue") {
    return "border-l-4 border-red-400 bg-red-50/30 hover:bg-red-50 dark:bg-red-950/10 dark:hover:bg-red-950/20";
  }

  if (state === "dueSoon") {
    return "border-l-4 border-amber-400 bg-amber-50/40 hover:bg-amber-50 dark:bg-amber-950/10 dark:hover:bg-amber-950/20";
  }

  return "border-l-4 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/60";
};

const StatusBadge = ({ container }) => {
  const label = container.status || "In Transit";
  const etaState = getEtaState(container);

  const styles = {
    "In Transit": "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    "Arrived at Port":
      "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
    Delivered:
      "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300",
    "Departed from Origin Port":
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    dueSoon:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  };

  const className =
    label === "Delivered"
      ? styles.Delivered
      : etaState === "overdue"
      ? styles.overdue
      : etaState === "dueSoon"
      ? styles.dueSoon
      : styles[label] || styles["In Transit"];

  return (
    <span
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-semibold leading-none ${className}`}
    >
      {label}
    </span>
  );
};

const ClearanceCheckbox = ({ checked, disabled, onChange }) => {
  return (
    <label className="inline-flex cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
};

const ClearanceBadge = ({ cleared }) => {
  return cleared ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
      <FiCheckCircle size={11} />
      Cleared
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
      <FiClock size={11} />
      Pending
    </span>
  );
};

function Toast({ message, type = "success", onClose }) {
  if (!message) return null;

  const isError = type === "error";

  return (
    <div className="fixed right-5 top-5 z-[10000] w-[calc(100%-40px)] max-w-md">
      <div
        className={`flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-2xl dark:bg-slate-900 ${
          isError
            ? "border-red-100 text-red-700 dark:border-red-900 dark:text-red-300"
            : "border-emerald-100 text-emerald-700 dark:border-emerald-900 dark:text-emerald-300"
        }`}
      >
        <div
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
            isError ? "bg-red-50 dark:bg-red-950" : "bg-emerald-50 dark:bg-emerald-950"
          }`}
        >
          {isError ? <FiAlertCircle /> : <FiCheckCircle />}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">{isError ? "Action failed" : "Success"}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{message}</p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <FiX />
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  danger = false,
  loading = false,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div
          className={`grid h-14 w-14 place-items-center rounded-2xl ${
            danger
              ? "bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300"
              : "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300"
          }`}
        >
          {danger ? <FiTrash className="text-xl" /> : <FiInfo className="text-xl" />}
        </div>

        <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
          {title}
        </h3>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          {message}
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={`rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:opacity-60 ${
              danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({
  container,
  onClose,
  formatDate,
  isSuperAdmin,
  customers,
  canReadDocuments,
  canUploadDocuments,
  canDeleteDocuments,
  canManageCollaborators,
  canUpdateStatus,
  limitedMode,
  initialSection = "details",
  onContainerUpdated,
  onMessage,
}) {
  const documentsSectionRef = useRef(null);

  useEffect(() => {
    if (!container || initialSection !== "documents") return undefined;

    const timer = window.setTimeout(() => {
      documentsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [container?.id, initialSection]);

  if (!container) return null;

  const assignedCustomer = customers.find(
    (customer) => String(customer.id) === String(container.customerId)
  );

  const baseRows = [
    ["Serial No", container.serialNo || "N/A"],
    ["Booking/BL No", container.bookingBLNo || "N/A"],
    ["Date", formatDate(container.date)],
    ["Container Number", container.containerNumber || "N/A"],
    ["Vessel Name", container.vesselName || "N/A"],
    ["Origin", container.origin || "N/A"],
    ["Destination", container.destination || "N/A"],
    ["Custom Cleared", container.customCleared ? "Yes" : "No"],
    ["Assigned Customer", assignedCustomer?.name || "Not Assigned"],
    ["Container Type", formatContainerType(container.containerType)],
    ["Consignee", container.consignee || "N/A"],
    ["ETA", formatDate(container.expectedArrival)],
    ["Delivery Booked On", formatDate(container.deliveryBookedOn)],
  ];

  if (isSuperAdmin) {
    baseRows.splice(6, 0, ["Shipper", container.shipper || "N/A"]);
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
              <FiBox className="text-xl" />
            </div>

            <div>
              <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                Container Details
              </h3>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {container.bookingBLNo || container.containerNumber || "Container"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FiX />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          <div className="mb-5 flex items-center gap-2">
            <StatusBadge container={container} />
            <ClearanceBadge cleared={container.customCleared} />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {baseRows.map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {label}
                </p>

                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {value}
                </p>
              </div>
            ))}

            <div className="md:col-span-2 rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Main Goods
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {container.mainGoods || "N/A"}
              </p>
            </div>

            <div className="md:col-span-2 rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Description
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {container.description || "N/A"}
              </p>
            </div>
          </div>

          <ContainerStatusUpdatePanel
            container={container}
            canUpdate={canUpdateStatus}
            limitedMode={limitedMode}
            onUpdated={onContainerUpdated}
            onMessage={onMessage}
          />

          <div ref={documentsSectionRef} className="scroll-mt-4">
            <ContainerDocumentsPanel
              containerId={container.id}
              canRead={canReadDocuments}
              canUpload={canUploadDocuments}
              canDelete={canDeleteDocuments}
              onMessage={onMessage}
            />
          </div>

          <ContainerCollaboratorsPanel
            containerId={container.id}
            canManage={canManageCollaborators}
            onMessage={onMessage}
          />
        </div>

        <div className="flex justify-end border-t border-slate-100 px-4 py-3 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Containers() {
  const [containers, setContainers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState("customer");

  const [assignCustomerId, setAssignCustomerId] = useState("");
  const [assigningCustomer, setAssigningCustomer] = useState(false);

  const [accessUsers, setAccessUsers] = useState([]);
  const [assignAccessUserId, setAssignAccessUserId] = useState("");
  const [assigningAccessUser, setAssigningAccessUser] = useState(false);
  const [accessPermissions, setAccessPermissions] = useState({
    canUpdateStatus: true,
    canUploadDocuments: true,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_LIMIT,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContainer, setEditingContainer] = useState(null);
  const [viewingContainer, setViewingContainer] = useState(null);
  const [detailInitialSection, setDetailInitialSection] = useState("details");
  const [deletingContainer, setDeletingContainer] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [clearingUpdatingId, setClearingUpdatingId] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    customCleared: "",
    fromDate: null,
    toDate: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const isSuperAdmin = checkIsSuperAdmin();
  const isCustomer = checkIsCustomer();
  const isAssignedAccess = checkIsAssignedAccess();
  const canCreateContainers = hasPermission("containers", "create") && !isCustomer && !isAssignedAccess;
  const canUpdateContainers = hasPermission("containers", "update") && !isCustomer;
  const canDeleteContainers = hasPermission("containers", "delete") && !isCustomer && !isAssignedAccess;
  const canManageAssignments = canUpdateContainers && !isAssignedAccess;
  const canReadDocuments = hasPermission("container-documents", "read");
  const canUploadDocuments = hasPermission("container-documents", "create");
  const canDeleteDocuments = hasPermission("container-documents", "delete") && !isAssignedAccess;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast({ message: "", type: "success" }), 2500);
  };

  function dateToInputValue(date) {
    if (!date) return "";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "";

    return d.toISOString().split("T")[0];
  }

  const formatDate = (date) => {
    if (!date) return "N/A";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "N/A";

    return d.toLocaleDateString("en-GB");
  };

  const getAssignedCustomerName = (customerId) => {
    if (!customerId) return "Not Assigned";

    const customer = customers.find(
      (item) => String(item.id) === String(customerId)
    );

    return customer?.name || `Customer #${customerId}`;
  };

  const loadAssignmentOptions = async () => {
    const [customersResult, accessUsersResult] = await Promise.allSettled([
      getAssignableCustomers(),
      api.get("/containers/access-users/assignable"),
    ]);

    if (customersResult.status === "fulfilled") {
      setCustomers(
        Array.isArray(customersResult.value.data?.customers)
          ? customersResult.value.data.customers
          : []
      );
    } else {
      console.error(
        "Error loading customers:",
        customersResult.reason
      );
      setCustomers([]);
    }

    if (accessUsersResult.status === "fulfilled") {
      setAccessUsers(
        Array.isArray(accessUsersResult.value.data?.users)
          ? accessUsersResult.value.data.users
          : []
      );
    } else {
      console.error(
        "Error loading assigned-access users:",
        accessUsersResult.reason
      );
      setAccessUsers([]);
    }
  };

  const fetchData = async (page = pagination.page) => {
    setLoading(true);

    try {
      const params = {
        page,
        limit: PAGE_LIMIT,
        search: searchTerm || undefined,
        status: filters.status || undefined,
        customCleared: filters.customCleared || undefined,
        fromDate: filters.fromDate ? dateToInputValue(filters.fromDate) : undefined,
        toDate: filters.toDate ? dateToInputValue(filters.toDate) : undefined,
      };

      const res = await getContainers(params);

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      const nextPagination = res.data?.pagination || {
        total: list.length,
        page,
        limit: PAGE_LIMIT,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      };

      setContainers(list);
      setSelectedIds([]);
      setAssignCustomerId("");
      setAssignAccessUserId("");
      setPagination(nextPagination);
    } catch (error) {
      console.error("Error fetching containers:", error);
      showToast(error.response?.data?.message || "Failed to load containers.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignmentOptions();
  }, []);

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    filters.status,
    filters.customCleared,
    filters.fromDate,
    filters.toDate,
  ]);

  const isAllSelected =
    containers.length > 0 &&
    containers.every((container) => selectedIds.includes(container.id));

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(containers.map((container) => container.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, id])));
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleBulkAssignCustomer = async () => {
    if (selectedIds.length === 0) {
      showToast("Please select at least one container.", "error");
      return;
    }

    if (!assignCustomerId) {
      showToast("Please select a customer.", "error");
      return;
    }

    setAssigningCustomer(true);

    try {
      await bulkAssignContainersToCustomer({
        containerIds: selectedIds,
        customerId: assignCustomerId,
      });

      showToast("Selected containers assigned to customer successfully.");
      setSelectedIds([]);
      setAssignCustomerId("");
      await fetchData(pagination.page);
    } catch (error) {
      console.error("Error assigning customer:", error);
      showToast(error.response?.data?.message || "Failed to assign customer.", "error");
    } finally {
      setAssigningCustomer(false);
    }
  };

  const handleBulkAssignAccessUser = async () => {
    if (selectedIds.length === 0) {
      showToast("Please select at least one container.", "error");
      return;
    }

    if (!assignAccessUserId) {
      showToast(
        "Please select a supplier or forwarder user.",
        "error"
      );
      return;
    }

    setAssigningAccessUser(true);

    try {
      const response = await api.patch(
        "/containers/bulk-assign-access",
        {
          containerIds: selectedIds,
          userId: Number(assignAccessUserId),
          canUpdateStatus: Boolean(
            accessPermissions.canUpdateStatus
          ),
          canUploadDocuments: Boolean(
            accessPermissions.canUploadDocuments
          ),
        }
      );

      const selectedUser = accessUsers.find(
        (item) =>
          String(item.id) === String(assignAccessUserId)
      );

      showToast(
        response.data?.message ||
          `${
            selectedUser?.name || "Selected user"
          } assigned to ${selectedIds.length} container(s).`
      );

      setSelectedIds([]);
      setAssignAccessUserId("");
      await fetchData(pagination.page);
    } catch (error) {
      console.error(
        "Error assigning supplier/forwarder access:",
        error
      );
      showToast(
        error.response?.data?.message ||
          "Failed to assign container access.",
        "error"
      );
    } finally {
      setAssigningAccessUser(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingContainer) {
        await updateContainer(editingContainer.id, data);
        showToast("Container updated successfully.");
      } else {
        await createContainer(data);
        showToast("Container created successfully.");
      }

      await fetchData(editingContainer ? pagination.page : 1);
      setEditingContainer(null);
      setShowModal(false);
    } catch (error) {
      console.error("Error saving container:", error);
      throw error;
    }
  };

  const requestDelete = (container) => setDeletingContainer(container);

  const confirmDelete = async () => {
    if (!deletingContainer?.id) return;

    setDeleteLoading(true);

    try {
      await deleteContainer(deletingContainer.id);
      await fetchData(pagination.page);
      showToast("Container deleted successfully.");
      setDeletingContainer(null);
    } catch (error) {
      console.error("Error deleting container:", error);
      showToast(error.response?.data?.message || "Failed to delete container.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCustomClearedToggle = async (container, checked) => {
    if (!container?.id) return;

    setClearingUpdatingId(container.id);

    const previousContainers = containers;

    setContainers((items) =>
      items.map((item) =>
        item.id === container.id ? { ...item, customCleared: checked } : item
      )
    );

    try {
      await updateContainer(container.id, { customCleared: checked });
      showToast(
        checked
          ? "Container marked as custom cleared."
          : "Custom cleared mark removed."
      );
      await fetchData(pagination.page);
    } catch (error) {
      console.error("Error updating custom cleared:", error);
      setContainers(previousContainers);
      showToast(
        error.response?.data?.message || "Failed to update custom cleared status.",
        "error"
      );
    } finally {
      setClearingUpdatingId(null);
    }
  };

  const handleEdit = (container) => {
    setEditingContainer(container);
    setShowModal(true);
  };

  const handleView = (container) => {
    setDetailInitialSection("details");
    setViewingContainer(container);
  };

  const handleOpenDocuments = (container) => {
    setDetailInitialSection("documents");
    setViewingContainer(container);
  };

  const canUploadToContainer = (container) =>
    canReadDocuments &&
    canUploadDocuments &&
    container?.accessCapabilities?.canUploadDocuments !== false;

  const handleDetailUpdated = (updated) => {
    if (!updated?.id) return;
    setViewingContainer(updated);
    setContainers((items) =>
      items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
    );
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      customCleared: "",
      fromDate: null,
      toDate: null,
    });
    setSearchTerm("");
  };

  const goToPage = (nextPage) => {
    if (loading) return;
    if (nextPage < 1 || nextPage > pagination.totalPages) return;

    fetchData(nextPage);
  };

  const statusOptions = [
    "In Transit",
    "Arrived at Port",
    "Delivered",
    "Departed from Origin Port",
  ];

  const baseInput =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  const card =
    "rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

  return (
    <div className="space-y-6 p-6 text-slate-900 dark:text-slate-100">
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />

      <ConfirmDialog
        open={Boolean(deletingContainer)}
        title="Delete container?"
        message={`Are you sure you want to delete "${
          deletingContainer?.bookingBLNo ||
          deletingContainer?.containerNumber ||
          "this container"
        }"? This action cannot be undone.`}
        confirmText="Delete Container"
        danger
        loading={deleteLoading}
        onCancel={() => setDeletingContainer(null)}
        onConfirm={confirmDelete}
      />

      <DetailModal
        container={viewingContainer}
        onClose={() => {
          setViewingContainer(null);
          setDetailInitialSection("details");
        }}
        formatDate={formatDate}
        isSuperAdmin={isSuperAdmin}
        customers={customers}
        canReadDocuments={canReadDocuments}
        canUploadDocuments={
          canUploadDocuments &&
          viewingContainer?.accessCapabilities?.canUploadDocuments !== false
        }
        canDeleteDocuments={canDeleteDocuments}
        canManageCollaborators={canManageAssignments}
        canUpdateStatus={
          canUpdateContainers &&
          viewingContainer?.accessCapabilities?.canUpdateStatus !== false
        }
        limitedMode={isAssignedAccess}
        initialSection={detailInitialSection}
        onContainerUpdated={handleDetailUpdated}
        onMessage={showToast}
      />

      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            Containers
          </h1>

          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage and track all shipping containers
          </p>
        </div>

        {canCreateContainers ? (
          <button
            type="button"
            onClick={() => {
              setEditingContainer(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-white shadow-sm transition hover:bg-blue-700"
          >
            <FiPlus size={18} />
            Add Container
          </button>
        ) : null}
      </div>

      <div className={`${card} p-4`}>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />

            <input
              type="text"
              placeholder="Search by serial, booking/BL no, container, vessel, origin, destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 transition ${
                showFilters
                  ? "border-blue-300 bg-blue-50 text-blue-600 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <FiFilter />
              Filters
            </button>

            <div className="flex overflow-hidden rounded-lg border border-slate-300 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`px-3 py-2 transition ${
                  viewMode === "table"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}
              >
                <FiList />
              </button>

              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`px-3 py-2 transition ${
                  viewMode === "grid"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}
              >
                <FiGrid />
              </button>
            </div>
          </div>
        </div>

        {showFilters ? (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 dark:border-slate-800 sm:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Status
              </label>

              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className={baseInput}
              >
                <option value="">All Status</option>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Custom Cleared
              </label>

              <select
                value={filters.customCleared}
                onChange={(e) =>
                  setFilters({ ...filters, customCleared: e.target.value })
                }
                className={baseInput}
              >
                <option value="">All</option>
                <option value="true">Cleared</option>
                <option value="false">Pending</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                From Date
              </label>

              <input
                type="date"
                value={dateToInputValue(filters.fromDate)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    fromDate: e.target.value ? new Date(e.target.value) : null,
                  })
                }
                className={baseInput}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                To Date
              </label>

              <input
                type="date"
                value={dateToInputValue(filters.toDate)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    toDate: e.target.value ? new Date(e.target.value) : null,
                  })
                }
                className={baseInput}
              />
            </div>

            {(filters.status ||
              filters.customCleared ||
              filters.fromDate ||
              filters.toDate ||
              searchTerm) && (
              <div className="flex justify-end sm:col-span-4">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                >
                  <FiX />
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {canManageAssignments && selectedIds.length > 0 ? (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-[210px]">
              <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                {selectedIds.length} container(s) selected
              </p>

              <p className="mt-1 text-xs leading-5 text-blue-600/80 dark:text-blue-300/80">
                Assign the selected containers to a customer or grant
                supplier/forwarder access.
              </p>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-3 lg:grid-cols-[190px_minmax(260px,1fr)_auto]">
              <select
                value={assignmentMode}
                onChange={(event) => {
                  setAssignmentMode(event.target.value);
                  setAssignCustomerId("");
                  setAssignAccessUserId("");
                }}
                className="rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-blue-800 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="customer">Customer</option>
                <option value="access">
                  Supplier / Forwarder
                </option>
              </select>

              {assignmentMode === "customer" ? (
                <select
                  value={assignCustomerId}
                  onChange={(event) =>
                    setAssignCustomerId(event.target.value)
                  }
                  className="min-w-0 rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-blue-800 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="">Select customer</option>

                  {customers.map((customer) => (
                    <option
                      key={customer.id}
                      value={customer.id}
                    >
                      {customer.name}
                      {customer.email
                        ? ` (${customer.email})`
                        : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={assignAccessUserId}
                  onChange={(event) =>
                    setAssignAccessUserId(event.target.value)
                  }
                  className="min-w-0 rounded-xl border border-violet-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-violet-900 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="">
                    Select supplier / forwarder
                  </option>

                  {accessUsers.length === 0 ? (
                    <option value="" disabled>
                      No assigned-access users found
                    </option>
                  ) : null}

                  {accessUsers.map((accessUser) => (
                    <option
                      key={accessUser.id}
                      value={accessUser.id}
                    >
                      {accessUser.name}
                      {accessUser.roleName
                        ? ` — ${accessUser.roleName}`
                        : ""}
                      {accessUser.email
                        ? ` (${accessUser.email})`
                        : ""}
                    </option>
                  ))}
                </select>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2">
                {assignmentMode === "customer" ? (
                  <button
                    type="button"
                    disabled={assigningCustomer}
                    onClick={handleBulkAssignCustomer}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FiUserCheck />
                    {assigningCustomer
                      ? "Assigning..."
                      : "Assign Customer"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={assigningAccessUser}
                    onClick={handleBulkAssignAccessUser}
                    className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FiUserCheck />
                    {assigningAccessUser
                      ? "Granting..."
                      : "Grant Access"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSelectedIds([]);
                    setAssignCustomerId("");
                    setAssignAccessUserId("");
                  }}
                  className="rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-300"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {assignmentMode === "access" ? (
            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-violet-200 bg-white/80 p-3 dark:border-violet-900 dark:bg-slate-950/70 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-violet-800 dark:text-violet-300">
                  Container access permissions
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  These permissions apply to every selected container
                  without changing the assigned customer.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={accessPermissions.canUpdateStatus}
                    onChange={(event) =>
                      setAccessPermissions((previous) => ({
                        ...previous,
                        canUpdateStatus: event.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  Update status
                </label>

                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={
                      accessPermissions.canUploadDocuments
                    }
                    onChange={(event) =>
                      setAccessPermissions((previous) => ({
                        ...previous,
                        canUploadDocuments:
                          event.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  Upload documents
                </label>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Showing{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {containers.length}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {pagination.total}
          </span>{" "}
          containers
        </p>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex h-3 w-3 rounded-full bg-green-400" />
          Delivered
          <span className="inline-flex h-3 w-3 rounded-full bg-amber-400" />
          ETA near
          <span className="inline-flex h-3 w-3 rounded-full bg-red-400" />
          ETA passed
        </div>
      </div>

      {viewMode === "table" ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="min-w-[1450px] w-full table-fixed text-sm">
              <colgroup>
                {canManageAssignments ? <col className="w-[42px]" /> : null}
                <col className="w-[65px]" />
                <col className="w-[132px]" />
                <col className="w-[95px]" />
                <col className="w-[128px]" />
                <col className="w-[155px]" />
                <col className="w-[85px]" />
                <col className="w-[92px]" />
                <col className="w-[92px]" />
                <col className="w-[120px]" />
                <col className="w-[105px]" />
                <col className="w-[125px]" />
                <col className="w-[135px]" />
                <col className="w-[150px]" />
              </colgroup>

              <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800">
                <tr>
                  {canManageAssignments ? (
                    <th className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  ) : null}

                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    S.No
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Booking/BL No
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Date
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Container No.
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Vessel Name
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Origin
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Destination
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    ETA
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Delivery Booked
                  </th>
                  <th className="px-2 py-3 text-center text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Custom Cleared
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Customer
                  </th>
                  <th className="px-2 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Status
                  </th>
                  <th className="px-2 py-3 text-right text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading ? (
                  <tr>
                    <td
                      colSpan={canManageAssignments ? 14 : 13}
                      className="px-3 py-12 text-center text-slate-500"
                    >
                      Loading containers...
                    </td>
                  </tr>
                ) : containers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canManageAssignments ? 14 : 13}
                      className="px-3 py-12 text-center text-slate-500"
                    >
                      No containers found
                    </td>
                  </tr>
                ) : (
                  containers.map((container) => (
                    <tr
                      key={container.id}
                      className={`transition ${getRowColorClass(container)}`}
                    >
                      {canManageAssignments ? (
                        <td className="px-2 py-2.5 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(container.id)}
                            onChange={(e) =>
                              toggleSelectOne(container.id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      ) : null}

                      <td className="whitespace-nowrap px-2 py-2.5 font-semibold text-slate-900 dark:text-slate-100">
                        {container.serialNo || "N/A"}
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 font-semibold text-slate-900 dark:text-slate-100">
                        {container.bookingBLNo || "N/A"}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {formatDate(container.date)}
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {container.containerNumber || "N/A"}
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {container.vesselName || "N/A"}
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {container.origin || "N/A"}
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {container.destination || "N/A"}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {formatDate(container.expectedArrival)}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2.5 text-slate-600 dark:text-slate-300">
                        {formatDate(container.deliveryBookedOn)}
                      </td>

                      <td className="px-2 py-2.5 text-center">
                        <div className="flex flex-col items-center justify-center gap-1">
                          <ClearanceCheckbox
                            checked={container.customCleared}
                            disabled={
                              !canUpdateContainers ||
                              container.accessCapabilities?.canUpdateStatus === false ||
                              clearingUpdatingId === container.id
                            }
                            onChange={(checked) =>
                              handleCustomClearedToggle(container, checked)
                            }
                          />

                          <ClearanceBadge cleared={container.customCleared} />
                        </div>
                      </td>

                      <td className="whitespace-normal break-words px-2 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                        {getAssignedCustomerName(container.customerId)}
                      </td>

                      <td className="px-3 py-2.5 align-middle">
                        <div className="flex items-center">
                          <StatusBadge container={container} />
                        </div>
                      </td>

                      <td className="border-l border-slate-100 px-3 py-2.5 align-middle dark:border-slate-800">
                        <div className="flex min-w-[132px] items-center justify-end gap-1.5 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleView(container)}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="View container details"
                            aria-label="View container details"
                          >
                            <FiEye size={16} />
                          </button>

                          {canUploadToContainer(container) ? (
                            <button
                              type="button"
                              onClick={() => handleOpenDocuments(container)}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-violet-600 transition hover:bg-violet-50 hover:text-violet-700 dark:text-violet-300 dark:hover:bg-violet-950 dark:hover:text-violet-200"
                              title="Upload container documents"
                              aria-label="Upload container documents"
                            >
                              <FiUploadCloud size={16} />
                            </button>
                          ) : null}

                          {canUpdateContainers && !isAssignedAccess ? (
                            <button
                              type="button"
                              onClick={() => handleEdit(container)}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-blue-600 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950"
                              title="Edit"
                            >
                              <FiEdit2 size={15} />
                            </button>
                          ) : null}

                          {canDeleteContainers ? (
                            <button
                              type="button"
                              onClick={() => requestDelete(container)}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950"
                              title="Delete"
                            >
                              <FiTrash size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full py-12 text-center text-slate-500">
              Loading containers...
            </div>
          ) : containers.length === 0 ? (
            <div className="col-span-full py-12 text-center text-slate-500">
              No containers found
            </div>
          ) : (
            containers.map((container) => (
              <ContainerCard
                key={container.id}
                container={container}
                customerName={getAssignedCustomerName(container.customerId)}
                onView={handleView}
                onUpload={
                  canUploadToContainer(container)
                    ? handleOpenDocuments
                    : undefined
                }
                onEdit={
                  canUpdateContainers && !isAssignedAccess
                    ? handleEdit
                    : undefined
                }
                onDelete={
                  canDeleteContainers
                    ? requestDelete
                    : undefined
                }
              />
            ))
          )}
        </div>
      ) : null}

      <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Page{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {pagination.page}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {pagination.totalPages}
          </span>
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!pagination.hasPrevPage || loading}
            onClick={() => goToPage(pagination.page - 1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiChevronLeft />
            Previous
          </button>

          <button
            type="button"
            disabled={!pagination.hasNextPage || loading}
            onClick={() => goToPage(pagination.page + 1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Next
            <FiChevronRight />
          </button>
        </div>
      </div>

      <AddContainerModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingContainer(null);
        }}
        onSave={handleSave}
        editingContainer={editingContainer}
        customers={customers}
      />
    </div>
  );
}